import JsBarcode from "jsbarcode"
import { PDFDocument, StandardFonts } from "pdf-lib"
import {
    PROCESSING_RECEIPT_ITEMS_PER_PAGE,
    PROCESSING_RECEIPT_LAYOUT,
    PROCESSING_RECEIPT_TEMPLATE_URLS,
    processingReceiptBaselineFromTop,
    processingReceiptYFromTop,
} from "./processingReceiptLayout.js"
import {
    computeLineItemColumnLayout,
    truncateTextToWidth,
} from "./processingReceiptLineLayout.js"
import { buildProcessingReceiptFromOrder } from "./buildProcessingReceiptFromOrder.js"

function wrapTextToWidth(text, font, size, maxWidth) {
    const trimmed = String(text ?? "").trim()
    if (!trimmed) return []
    if (font.widthOfTextAtSize(trimmed, size) <= maxWidth) return [trimmed]

    const lines = []
    let current = ""

    const pushCurrent = () => {
        if (current) {
            lines.push(current)
            current = ""
        }
    }

    for (const word of trimmed.split(/\s+/)) {
        const candidate = current ? `${current} ${word}` : word
        if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
            current = candidate
            continue
        }

        pushCurrent()

        if (font.widthOfTextAtSize(word, size) <= maxWidth) {
            current = word
            continue
        }

        let chunk = ""
        for (const ch of word) {
            const next = chunk + ch
            if (font.widthOfTextAtSize(next, size) > maxWidth && chunk) {
                lines.push(chunk)
                chunk = ch
            } else {
                chunk = next
            }
        }
        current = chunk
    }

    pushCurrent()
    return lines
}

function drawAddressBlock(page, addr, x, font, fontBold, maxWidth) {
    const { address } = PROCESSING_RECEIPT_LAYOUT
    const fields = [
        { text: addr.name, size: address.nameSize, activeFont: fontBold },
        { text: addr.addressLine1, size: address.size, activeFont: font },
        ...(addr.addressLine2
            ? [{ text: addr.addressLine2, size: address.size, activeFont: font }]
            : []),
        { text: addr.phone, size: address.size, activeFont: font },
        { text: addr.email, size: address.size, activeFont: font },
    ]

    let yFromTop = address.startYFromTop
    for (const field of fields) {
        const wrapped = wrapTextToWidth(field.text, field.activeFont, field.size, maxWidth)
        for (const line of wrapped) {
            page.drawText(line, {
                x,
                y: processingReceiptBaselineFromTop(yFromTop, field.size),
                size: field.size,
                font: field.activeFont,
            })
            yFromTop += address.lineHeight
        }
    }
}

function chunkLines(items, size) {
    const pages = []
    for (let i = 0; i < items.length; i += size) {
        pages.push(items.slice(i, i + size))
    }
    return pages.length > 0 ? pages : [[]]
}

async function barcodePngBytes(value) {
    if (typeof document === "undefined") return null
    try {
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        JsBarcode(svg, value, {
            format: "CODE128",
            width: 1.35,
            height: 52,
            displayValue: false,
            margin: 2,
            xmlDocument: document,
        })
        const svgStr = new XMLSerializer().serializeToString(svg)
        const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`
        const img = await new Promise((resolve, reject) => {
            const el = new Image()
            el.onload = () => resolve(el)
            el.onerror = () => reject(new Error("Barcode image failed"))
            el.src = url
        })
        const canvas = document.createElement("canvas")
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        const ctx = canvas.getContext("2d")
        if (!ctx) return null
        ctx.drawImage(img, 0, 0)
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"))
        if (!blob) return null
        return new Uint8Array(await blob.arrayBuffer())
    } catch {
        return null
    }
}

async function loadTemplatePdf(url) {
    const res = await fetch(url)
    if (!res.ok) {
        throw new Error(`Failed to load processing receipt template (${res.status})`)
    }
    return PDFDocument.load(await res.arrayBuffer())
}

function drawProcessingLines(page, lines, font, columns) {
    const { items } = PROCESSING_RECEIPT_LAYOUT
    const layout = columns ?? computeLineItemColumnLayout(lines, font, items)

    lines.forEach((line, i) => {
        const rowTop = items.startYFromTop + i * items.rowHeight
        const baseY = processingReceiptYFromTop(rowTop + items.rowBaselineOffset)
        const speciesText = truncateTextToWidth(
            line.species,
            font,
            layout.fontSize,
            layout.columnWidths.species
        )
        const processText = truncateTextToWidth(
            line.process,
            font,
            layout.fontSize,
            layout.columnWidths.process
        )

        page.drawText(speciesText, {
            x: layout.speciesX,
            y: baseY,
            size: layout.fontSize,
            font,
        })
        page.drawText(processText, {
            x: layout.processX,
            y: baseY,
            size: layout.fontSize,
            font,
        })
        const qty = `x ${line.quantity}`
        page.drawText(qty, {
            x: layout.qtyX,
            y: baseY,
            size: layout.fontSize,
            font,
        })
    })
}

function drawFooter(page, opts, fontBold) {
    const { footer } = PROCESSING_RECEIPT_LAYOUT
    const baseY = processingReceiptBaselineFromTop(footer.yFromTop, footer.size)
    page.drawText(`${opts.pageIndex} of ${opts.pageCount}`, {
        x: footer.pageX,
        y: baseY,
        size: footer.size,
        font: fontBold,
    })
    page.drawText(String(opts.boxBagCount), {
        x: footer.boxesX,
        y: baseY,
        size: footer.size,
        font: fontBold,
    })
    const depot = `${opts.depotWeightLbs} LBS`
    page.drawText(depot, {
        x: footer.depotX - fontBold.widthOfTextAtSize(depot, footer.size),
        y: baseY,
        size: footer.size,
        font: fontBold,
    })
}

async function overlayReceiptPage(
    templateUrl,
    receipt,
    pageLines,
    pageIndex,
    pageCount,
    barcodeBytes,
    lineColumns
) {
    const template = await loadTemplatePdf(templateUrl)
    const doc = await PDFDocument.create()
    const [copied] = await doc.copyPages(template, [0])
    doc.addPage(copied)
    const page = doc.getPage(0)
    const font = await doc.embedFont(StandardFonts.Helvetica)
    const fontBold = await doc.embedFont(StandardFonts.HelveticaBold)
    const { masterNumber, shipToRegion, barcode } = PROCESSING_RECEIPT_LAYOUT

    page.drawText(receipt.masterNumber, {
        x: masterNumber.x,
        y: processingReceiptBaselineFromTop(masterNumber.yFromTop, masterNumber.size),
        size: masterNumber.size,
        font: fontBold,
    })

    if (barcodeBytes) {
        const png = await doc.embedPng(barcodeBytes)
        page.drawImage(png, {
            x: barcode.x,
            y: processingReceiptYFromTop(barcode.yFromTop + barcode.height),
            width: barcode.width,
            height: barcode.height,
        })
    }

    page.drawText(receipt.shipToRegion, {
        x: shipToRegion.x,
        y: processingReceiptBaselineFromTop(shipToRegion.yFromTop, shipToRegion.size),
        size: shipToRegion.size,
        font: fontBold,
    })

    const { address } = PROCESSING_RECEIPT_LAYOUT
    const twoColumn = Boolean(receipt.alternateShipTo)
    drawAddressBlock(
        page,
        receipt.customer,
        address.leftX,
        font,
        fontBold,
        twoColumn ? address.maxWidthColumn : address.maxWidthSingle
    )
    if (receipt.alternateShipTo) {
        drawAddressBlock(
            page,
            receipt.alternateShipTo,
            address.rightX,
            font,
            fontBold,
            address.maxWidthColumn
        )
    }

    drawProcessingLines(page, pageLines, font, lineColumns)

    drawFooter(
        page,
        {
            pageIndex,
            pageCount,
            boxBagCount: receipt.boxBagCount,
            depotWeightLbs: receipt.depotWeightLbs,
        },
        fontBold
    )

    return doc
}

/**
 * Build a filled 4×6 mini processing receipt PDF from St. Jean's blank templates.
 *
 * @param {import("./buildProcessingReceiptFromOrder.js").ProcessingReceipt} receipt
 */
export async function buildProcessingReceiptPdf(receipt) {
    const linePages = chunkLines(receipt.lines, PROCESSING_RECEIPT_ITEMS_PER_PAGE)
    const pageCount = linePages.length
    const barcodeBytes = await barcodePngBytes(receipt.masterNumber)
    const output = await PDFDocument.create()
    const measureFont = await output.embedFont(StandardFonts.Helvetica)
    const lineColumns = computeLineItemColumnLayout(
        receipt.lines,
        measureFont,
        PROCESSING_RECEIPT_LAYOUT.items
    )

    for (let i = 0; i < pageCount; i++) {
        const templateUrl = i === 0
            ? PROCESSING_RECEIPT_TEMPLATE_URLS.firstPage
            : PROCESSING_RECEIPT_TEMPLATE_URLS.lastOfMulti

        const pageDoc = await overlayReceiptPage(
            templateUrl,
            receipt,
            linePages[i],
            i + 1,
            pageCount,
            barcodeBytes,
            lineColumns
        )

        const [page] = await output.copyPages(pageDoc, [0])
        output.addPage(page)
    }

    return output.save()
}

/**
 * Append mini processing receipt pages to an existing PDF byte array.
 *
 * @param {Uint8Array} labelPdfBytes
 * @param {import("./buildProcessingReceiptFromOrder.js").ProcessingReceipt} receipt
 */
export async function mergeLabelPdfWithProcessingReceipt(labelPdfBytes, receipt) {
    const receiptBytes = await buildProcessingReceiptPdf(receipt)
    const merged = await PDFDocument.create()
    const labelDoc = await PDFDocument.load(labelPdfBytes)
    const receiptDoc = await PDFDocument.load(receiptBytes)

    const labelPages = await merged.copyPages(labelDoc, labelDoc.getPageIndices())
    labelPages.forEach((page) => merged.addPage(page))

    const receiptPages = await merged.copyPages(receiptDoc, receiptDoc.getPageIndices())
    receiptPages.forEach((page) => merged.addPage(page))

    return merged.save()
}

/**
 * Interleave processing receipts with their order's box labels in a single PDF.
 *
 * The label PDF pages are assumed to be grouped per order in the same order as
 * `segments`. Each segment's receipt is inserted immediately after that order's
 * label pages, e.g. [order1 labels][order1 receipt][order2 labels][order2 receipt].
 *
 * @param {Uint8Array} labelPdfBytes
 * @param {{ pageCount: number, receipt: import("./buildProcessingReceiptFromOrder.js").ProcessingReceipt }[]} segments
 */
export async function mergeLabelPdfWithReceiptsInterleaved(labelPdfBytes, segments) {
    const merged = await PDFDocument.create()
    const labelDoc = await PDFDocument.load(labelPdfBytes)
    const totalLabelPages = labelDoc.getPageCount()
    let cursor = 0

    const copyLabelRange = async (count) => {
        const indices = []
        for (let i = 0; i < count && cursor < totalLabelPages; i++, cursor++) {
            indices.push(cursor)
        }
        if (indices.length === 0) return
        const pages = await merged.copyPages(labelDoc, indices)
        pages.forEach((page) => merged.addPage(page))
    }

    for (const segment of segments ?? []) {
        await copyLabelRange(Math.max(0, segment?.pageCount || 0))
        if (segment?.receipt) {
            const receiptBytes = await buildProcessingReceiptPdf(segment.receipt)
            const receiptDoc = await PDFDocument.load(receiptBytes)
            const receiptPages = await merged.copyPages(receiptDoc, receiptDoc.getPageIndices())
            receiptPages.forEach((page) => merged.addPage(page))
        }
    }

    // Safety: append any label pages not accounted for by the segments.
    await copyLabelRange(totalLabelPages - cursor)

    return merged.save()
}

/**
 * Build and open a processing-receipt PDF only (no box labels).
 *
 * @param {any} order
 * @param {number} totalBoxes
 * @param {any} app
 * @param {any} helpers
 */
export async function openProcessingReceiptPdf(order, totalBoxes, app, helpers) {
    const receipt = buildProcessingReceiptFromOrder(order, totalBoxes, app, helpers)
    const pdfBytes = await buildProcessingReceiptPdf(receipt)
    const blob = new Blob([pdfBytes], { type: "application/pdf" })
    const fileUrl = URL.createObjectURL(blob)
    window.open(fileUrl)
}

/**
 * Build and open a single PDF containing processing receipts for multiple orders.
 *
 * @param {any[]} orders
 * @param {any} app
 * @param {any} helpers
 */
export async function openProcessingReceiptsPdf(orders, app, helpers) {
    const merged = await PDFDocument.create()

    for (const order of orders ?? []) {
        const receipt = buildProcessingReceiptFromOrder(
            order,
            order?.total_boxes ?? 1,
            app,
            helpers
        )
        const receiptBytes = await buildProcessingReceiptPdf(receipt)
        const receiptDoc = await PDFDocument.load(receiptBytes)
        const pages = await merged.copyPages(receiptDoc, receiptDoc.getPageIndices())
        pages.forEach((page) => merged.addPage(page))
    }

    const pdfBytes = await merged.save()
    const blob = new Blob([pdfBytes], { type: "application/pdf" })
    const fileUrl = URL.createObjectURL(blob)
    window.open(fileUrl)
}
